import { useEffect, useMemo, useState } from "react";
import { ITransferListItem } from "@miplatform/types";
import { useDebounce } from "@miplatform/hooks";
import {
  useInspectionAuthorities,
  useInspectionTemplates,
  useCommunityInspectionAuthorities,
} from "@mibuildweb/hooks";

export const useCommunityInspectionAuthoritiesTabViewModel = (communityId: string) => {
  const [activeAuthorityId, setActiveAuthorityId] = useState<string | null>(null);
  const [isAddingAuthority, setIsAddingAuthority] = useState(false);
  const [authoritySearch, setAuthoritySearch] = useState("");
  const debouncedAuthoritySearch = useDebounce(authoritySearch, 300);

  const {
    inspectionAuthorities,
    setInspectionAuthorities,
    getInspectionAuthorities,
    isLoading: isLoadingAuthorities,
  } = useInspectionAuthorities();
  const { inspectionTemplates, getInspectionTemplates } = useInspectionTemplates();

  const {
    communityInspectionAuthorities,
    templates,
    isLoading,
    getCommunityInspectionAuthorities,
    addCommunityInspectionAuthority,
    removeCommunityInspectionAuthority,
    getTemplates,
    addTemplate,
    removeTemplate,
  } = useCommunityInspectionAuthorities();

  useEffect(() => {
    getInspectionTemplates();
    getCommunityInspectionAuthorities(communityId);
  }, [communityId]);

  useEffect(() => {
    if (isAddingAuthority) {
      getInspectionAuthorities({ search: debouncedAuthoritySearch, top: 10 });
    } else {
      setInspectionAuthorities([]);
    }
  }, [debouncedAuthoritySearch, isAddingAuthority]);

  useEffect(() => {
    if (communityInspectionAuthorities.length === 0) {
      setActiveAuthorityId(null);
      return;
    }

    if (
      !activeAuthorityId ||
      !communityInspectionAuthorities.some((a) => a.id === activeAuthorityId)
    ) {
      setActiveAuthorityId(communityInspectionAuthorities[0].id);
    }

    communityInspectionAuthorities.forEach((a) => getTemplates(a.id));
  }, [communityInspectionAuthorities, activeAuthorityId]);

  const assignedAuthorityIds = useMemo(
    () => new Set(communityInspectionAuthorities.map((a) => a.inspectionAuthorityId)),
    [communityInspectionAuthorities]
  );

  const availableToAdd = useMemo(
    () => inspectionAuthorities.filter((a) => !assignedAuthorityIds.has(a.id)),
    [inspectionAuthorities, assignedAuthorityIds]
  );

  const handleAuthorityTabChange = (tabValue: string) => {
    setActiveAuthorityId(tabValue);
  };

  const handleAddAuthority = async (inspectionAuthorityId: string) => {
    const response = await addCommunityInspectionAuthority({
      communityId,
      inspectionAuthorityId,
    });
    if (response?.data) {
      setActiveAuthorityId(response.data.id);
      setIsAddingAuthority(false);
    }
  };

  const handleRemoveAuthority = async (id: string) => {
    await removeCommunityInspectionAuthority(id);
    if (activeAuthorityId === id) {
      const next = communityInspectionAuthorities.find((a) => a.id !== id);
      setActiveAuthorityId(next?.id ?? null);
    }
  };

  const handleAddTemplate = async (workTemplateBaseId: string) => {
    if (!activeAuthorityId) return;
    await addTemplate({ communityInspectionAuthorityId: activeAuthorityId, workTemplateBaseId });
  };

  const handleRemoveTemplate = async (workTemplateBaseId: string) => {
    const template = templates.find(
      (t) =>
        t.communityInspectionAuthorityId === activeAuthorityId &&
        t.workTemplateBaseId === workTemplateBaseId
    );
    if (!template) return;
    await removeTemplate(template.id);
  };

  const authorityTabs = communityInspectionAuthorities.map((a) => ({
    label: a.inspectionAuthorityName,
    value: a.id,
    chipLabel: templates.filter((t) => t.communityInspectionAuthorityId === a.id).length,
  }));

  const selectedTemplateIds = useMemo(
    () =>
      new Set(
        templates
          .filter((t) => t.communityInspectionAuthorityId === activeAuthorityId)
          .map((t) => t.workTemplateBaseId)
      ),
    [templates, activeAuthorityId]
  );

  const availableInspections = useMemo<ITransferListItem[]>(
    () =>
      inspectionTemplates
        .filter((t) => !selectedTemplateIds.has(t.id))
        .map((t) => ({ key: t.id, title: t.name })),
    [inspectionTemplates, selectedTemplateIds]
  );

  const selectedInspections = useMemo<ITransferListItem[]>(
    () =>
      templates
        .filter((t) => t.communityInspectionAuthorityId === activeAuthorityId)
        .map((t) => ({
          key: t.workTemplateBaseId,
          title: t.workTemplateBaseName,
        })),
    [templates, activeAuthorityId]
  );

  return {
    activeAuthorityId,
    isAddingAuthority,
    setIsAddingAuthority,
    communityInspectionAuthorities,
    isLoading,
    availableToAdd,
    authoritySearch,
    setAuthoritySearch,
    isLoadingAuthorities,
    authorityTabs,
    availableInspections,
    selectedInspections,
    handleAuthorityTabChange,
    handleAddAuthority,
    handleRemoveAuthority,
    handleAddTemplate,
    handleRemoveTemplate,
  };
};
